import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check } from "lucide-react";
import api, { unwrap, apiError } from "@/lib/api";
import { EP } from "@/lib/endpoints";
import { useAuth } from "@/context/AuthContext";
import OnboardingShell from "@/pages/onboarding/OnboardingShell";
import { PrimaryButton, ErrorBox } from "@/components/AuthKit";

const MIN = 3;
const MAX = 10;

/**
 * Interests — 1:1 port of iOS `src/app/(onboarding)/interests.tsx`.
 * 2-column tile grid, brand-purple ring + soft brand bg + check badge when
 * active. 3-10 selections required.
 */
export default function Interests() {
  const navigate = useNavigate();
  const { refresh } = useAuth();

  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(EP.interestsList);
        const data = unwrap(res.data);
        const list = Array.isArray(data) ? data : data?.interests || data?.items || [];
        setItems(
          list.map((it) => ({
            id: it.id ?? it.interest_id ?? it._id,
            name: it.name || it.title || it.label || it.slug || `#${it.id}`,
            description: it.description,
          }))
        );
      } catch (e) {
        setErr(apiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const toggle = (id) => {
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(id)) {
        n.delete(id);
      } else {
        if (n.size >= MAX) return prev;
        n.add(id);
      }
      return n;
    });
  };

  const submit = async () => {
    if (selected.size < MIN) return setErr(`Choose at least ${MIN} interests.`);
    setSaving(true);
    setErr("");
    try {
      await api.put(EP.setInterests, { interest_ids: Array.from(selected) });
      await refresh();
      navigate("/onboarding/people");
    } catch (e) {
      setErr(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <OnboardingShell
      step="interests"
      title="What are you into?"
      subtitle={`Pick ${MIN} to ${MAX}. We'll use these to shape your feed.`}
      testId="onboarding-interests-screen"
    >
      {loading ? (
        <div className="grid grid-cols-2 gap-2.5">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="h-[76px] rounded-2xl bg-secondary animate-pulse" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div
          className="rounded-3xl border border-border p-7 text-center"
          data-testid="onboard-interests-empty"
        >
          <p className="text-muted-foreground text-[15px]">
            No interests catalogue is available yet. You can continue.
          </p>
          <PrimaryButton
            label="Continue"
            onClick={() => navigate("/onboarding/people")}
            className="mt-5 w-full"
            testId="onboard-interests-continue-empty"
          />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-2.5">
            {items.map((it) => {
              const active = selected.has(it.id);
              return (
                <button
                  key={String(it.id)}
                  type="button"
                  onClick={() => toggle(it.id)}
                  className={`relative rounded-[18px] border-[1.5px] p-4 text-left transition-all overflow-hidden min-h-[76px] ${
                    active
                      ? "border-[#7638FA] bg-[rgba(118,56,250,0.08)]"
                      : "border-border bg-card hover:border-foreground/25"
                  }`}
                  data-testid={`interest-tile-${it.id}`}
                >
                  {active && (
                    <div className="absolute top-2 right-2 w-[22px] h-[22px] rounded-full bg-[#7638FA] flex items-center justify-center">
                      <Check size={13} color="#fff" strokeWidth={3} />
                    </div>
                  )}
                  <div className="text-foreground text-[15px] font-semibold pr-6 line-clamp-2">
                    {it.name}
                  </div>
                  {it.description && (
                    <div className="text-muted-foreground text-xs mt-1 line-clamp-2">
                      {it.description}
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          <p
            className="text-muted-foreground text-sm mt-4"
            data-testid="onboard-interests-count"
          >
            {selected.size} of {MAX} selected
          </p>

          {err && (
            <div className="mt-4">
              <ErrorBox testId="onboard-interests-error">{err}</ErrorBox>
            </div>
          )}

          <PrimaryButton
            label="Continue"
            onClick={submit}
            loading={saving}
            disabled={selected.size < MIN}
            className="w-full mt-5"
            testId="onboard-interests-submit"
          />
        </>
      )}
    </OnboardingShell>
  );
}
